import {
  effectiveFlowLexicon,
  flowLexiconLabel,
} from '../lib/flowPractice';
import { useEnterAction } from '../lib/useEnterAction';
import type { FlowLexicon, UserPreferencesPatch, UserProfile } from '../types';

interface FlowPracticeSetupProps {
  user: UserProfile;
  lexicon: FlowLexicon;
  onLexiconChange: (lexicon: FlowLexicon) => void;
  onContinue: () => void;
  onCancel: () => void;
  onPreferencesChange?: (patch: UserPreferencesPatch) => void;
}

export default function FlowPracticeSetup({
  user,
  lexicon,
  onLexiconChange,
  onContinue,
  onCancel,
  onPreferencesChange,
}: FlowPracticeSetupProps) {
  const handleStart = () => {
    onPreferencesChange?.({ flowLexicon: lexicon });
    onContinue();
  };

  useEnterAction(handleStart);

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-6 animate-fade-in-up">
      <header className="text-center">
        <p className="label">Træningsbane</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          Vælg sprog
        </h1>
        <p className="mt-2 text-sm text-muted">
          Skriv så længe du har lyst. Korte tekster med hele alfabetet — stop
          når du er færdig med Færdig eller Escape.
        </p>
      </header>

      <section className="surface p-5">
        <p className="label">Sprog</p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-center">
          {(['da', 'en'] as const).map((id) => (
            <label
              key={id}
              className={`flex cursor-pointer items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ring-1 transition ${
                lexicon === id
                  ? 'bg-indigo-500 text-white ring-indigo-500'
                  : 'surface-soft text-strong ring-zinc-200 hover:ring-indigo-300 dark:ring-white/10'
              }`}
            >
              <input
                type="radio"
                name="flow-lexicon"
                value={id}
                checked={lexicon === id}
                onChange={() => onLexiconChange(id)}
                className="sr-only"
              />
              {flowLexiconLabel(id)}
            </label>
          ))}
        </div>
        <p className="mt-3 text-xs text-faint">
          Valget huskes til næste gang (
          {flowLexiconLabel(effectiveFlowLexicon(user))} sidst). Engelsk træner
          ofte c, x og z — dansk bruger æ, ø og å mere.
        </p>
      </section>

      <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <button
          type="button"
          autoFocus
          onClick={handleStart}
          className="rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30"
        >
          Start træningsbanen
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Tilbage
        </button>
      </div>
      <p className="text-center text-xs text-faint">Tryk Enter for at starte</p>
    </div>
  );
}
